import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, ViewChild,
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {DxTextBoxComponent, DxDataGridComponent} from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import {dxDataGridColumn} from 'devextreme/ui/data_grid';
import {of, Subject} from 'rxjs';
import {debounceTime, take, takeUntil} from 'rxjs/operators';
import {SearchableService} from '../../services/searchable.service';

@Component({
  selector: 'app-form-relation-glass',
  templateUrl: './form-relation-glass.component.html',
  styleUrls: ['./form-relation-glass.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormRelationGlassComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('container', {static: true}) public containerDiv?: ElementRef;
  @ViewChild('controls', {static: true}) public controlsDiv?: ElementRef;
  @ViewChild('grid', {static: true}) public grid?: DxDataGridComponent;
  @ViewChild('textSelector', {static: true}) public textSelector?: DxTextBoxComponent;
  @Input() public optionalConfig?: {
    button: { text: string; icon: string; disabled: boolean; filterExpr?: () => any | null | undefined };
  };
  @Input() public label?: string;
  @Input() public searchEnabled = false;
  @Input() public searchService?: SearchableService | DataSource;
  @Input() public disabled = false;
  @Input() public maxHeight: string | number | undefined = '100%';
  @Input() public dataSource: DataSource = new DataSource<any,any>([]);
  @Input() public alternateDataSource: DataSource = new DataSource<any,any>([]);
  @Input() public controlName?: string;
  @Input() public keyExpr = 'id';
  @Input() public textSelectorPlaceholder: string = '';
  @Input() public textSelectorExpr?: string;
  @Input() public columns: Array<dxDataGridColumn | string> = [];
  @Input() public filterPanel = false;
  @Input() public control?: UntypedFormControl;
  public currentDataSource: DataSource = new DataSource<any,any>([]);
  @Output() public selectionChanged = new EventEmitter();
  private onDestroy$: Subject<void> = new Subject<void>();
  @Input() public gridHeight: number | string = '100%';

  public selectedKeys: number[] = [];
  public selectedRowKeys: number[] = [];
  public filter: any[] = ['id', '=', 0];
  public isPopupVisible = false;
  public truncated = false;

  constructor(private ref: ChangeDetectorRef) {
    this.updateDataSource = this.updateDataSource.bind(this);
    this.refresh = this.refresh.bind(this);
  }

  public ngOnInit() {
    this.currentDataSource = new DataSource({store: this.dataSource?.store()});
    of(null)
      .pipe(take(1), debounceTime(1000))
      .subscribe(() => {
        this.control?.valueChanges
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(keys => {
            if (!keys) return;
            const isEquals = keys.length === this.selectedKeys.length &&
              this.selectedKeys.every((e, i) => e === keys[i]);
            if (!isEquals) {
              this.selectedKeys = keys;
              this.updateDataSource();
            }
          });
      });

    this.control?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.selectedKeys = [];
      });
  }

  public updateDataSource() {
    if (!this.selectedKeys) return;
    const filters = this.selectedKeys?.map(k => [this.keyExpr, '=', k]);
    const filter: any[] = [];
    filters?.forEach((v, i) => {
      filter.push(v);
      if (i < filters.length - 1) {
        filter.push('or');
      }
    });
    if (filter.length > 0) {
      this.filter = filter;
    } else {
      this.filter = [this.keyExpr, '=', -1];
    }

    this.grid?.instance.getDataSource().filter([this.filter]);
    this.grid?.instance
      .getDataSource()
      .load()
      .then(() => {
        this.grid?.instance.refresh()
          .then(() => {
            if (this.ref) {
              this.ref.detectChanges();
            }
          });
      });
  }

  public closeDialog(selection: any) {
    if (!selection) {
      this.isPopupVisible = false;
      this.selectedKeys = this.control?.value;
    } else {
      this.isPopupVisible = false;
      this.selectedKeys = this.control?.value ? this.control?.value : [];
      for (const item of selection) {
        if (this.selectedKeys.indexOf(item) === -1) {
          this.selectedKeys.push(item);
        }
      }
    }
    this.updateDataSource();
    this.control?.setValue(this.selectedKeys);
  }

  public async pickerEnterKeyEvent() {
    const text = this.textSelector?.text.toUpperCase();/*.replace('.', '');*/
    // @ts-ignore
    const request = this.searchService?.search(text).pipe(take(1));
    request?.subscribe((items: any) => {
      if (items.data && items.data.length > 0) {
        const selected = this.control?.value;
        selected?.push(items.data[0][this.keyExpr]);
        this.selectedKeys = selected;
        this.updateDataSource();
      }
      this.textSelector?.instance.reset();
    });
  }

  public removeItem() {
    const selected = this.selectedRowKeys;
    const values = this.control?.value;
    for (const selectedKey of selected) {
      const index = values.findIndex((k: any) => selectedKey === k);
      if (index !== -1) {
        values.splice(index, 1);
      }
    }
    this.updateDataSource();
    this.control?.setValue(values);
    this.selectedRowKeys = [];
  }

  public search(truncated: boolean = false) {
    this.isPopupVisible = true;
    this.truncated = truncated;
  }

  public get FormRelationTitle() {
    return `Select ${this.label}`;
  }

  public get IsSelected(): boolean {
    return this.selectedRowKeys.length > 0;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public ngAfterViewInit(): void {
    if (this.control?.value) {
      const keys = this.control?.value;
      const isEquals = keys.length === this.selectedKeys.length &&
        this.selectedKeys.every((e, i) => e === keys[i]);
      if (!isEquals) {
        this.selectedKeys = keys;
      }
    }
    this.updateDataSource();
    if (this.control?.value) {
      this.control.setValue(this.control.value);
    }
    this.refresh();

    if (typeof this.maxHeight === 'number' && this.controlsDiv) {
      this.gridHeight = this.maxHeight - this.controlsDiv.nativeElement.clientHeight - 32;
    } else {
      this.gridHeight = '100%';
    }
  }

  public refresh() {
    if (this.ref) {
      this.ref.detectChanges();
    }
  }
}
